import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/env';
import { Link } from 'react-router-dom';
import { useQueue } from '../../context/QueueContext';
import { exportToExcel } from '../../utils/exportToExcel';
import { getDateRangeForDays } from '../../utils/dateRangeUtils';

export default function AgingSummaryTable() {
  const [stationRows, setStationRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Re-fetch whenever a queued job finishes
  const { dashboardRefreshKey } = useQueue();

  const detailLink = (typeKey, daysRange) => {
    // This tells the list endpoint to use the same all-time pending scope as
    // the Aging Summary, rather than the dashboard's currently selected dates.
    let url = `/characters?type=${typeKey}&loc=remain&aging=1`;

    if (daysRange) {
      url += `&days=${daysRange}`;
      const range = getDateRangeForDays(daysRange);
      if (range) {
        url += `&sdate=${range.sdate}&edate=${range.edate}`;
      }
    } else {
      const range = getDateRangeForDays('all');
      if (range) {
        url += `&sdate=${range.sdate}&edate=${range.edate}`;
      }
    }

    return url;
  };

  useEffect(() => {
    loadStationData();
  }, [dashboardRefreshKey]);

  async function loadStationData() {
    setLoading(true);
    setError('');

    try {
      const res = await axios.get(
        `${API_BASE}/PendingDurationSummary`
      );

      if (res.data.result && res.data.result.length > 0) {
        setStationRows(res.data.result);
      }
    } catch (e) {
      console.warn(
        'Unable to load aging summary data:',
        e.message
      );

      setError('Unable to load aging summary data.');
    } finally {
      setLoading(false);
    }
  }

  function handleExcelExport() {
    const headers = [
      'SNo.',
      'Application Type',
      'Total Pending',
      'Within 10 Days',
      'Between 11-15 Days',
      'Between 16-30 Days',
      'Between 31-90 Days',
      'Between 91-180 Days',
      'Between 181-365 Days',
      'Above 01 Year',
    ];
    const data = stationRows.map((r, i) => [
      r.sno || i + 1,
      r.ApplicationType,
      r.Total,
      r.Within10Days,
      r.Between11To15Days,
      r.Between16To30Days,
      r.Between31To90Days,
      r.Between91To180Days,
      r.Between181To365Days,
      r.Above01Year,
    ]);
    exportToExcel({
      title: 'Aging Summary',
      headers,
      data,
      fileName: 'Aging_Summary',
      sheetName: 'Aging Summary',
    });
  }

  return (
    <div className="table-wrapper image-styled-table-wrapper card-upgrade">
      <div className="table-header-title">
        <h3>
          📊 Aging Summary
        </h3>
        <button
          className="btn-excel-export"
          onClick={handleExcelExport}
          title="Download Excel"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8.5 18l-2-3h1.2l1.3 2 1.3-2H11.5l-2 3 2.2 3H10.3l-1.3-2-1.3 2H6.3l2.2-3zm5 3v-6h1v5h2v1h-3z"/>
          </svg>
          Excel
        </button>
      </div>

      <table className="image-styled-table">
        <thead>
          <tr>
            <th className="th-sno">SNo.</th>
            <th className="th-app-type">Application Type</th>
            <th>Total <br/> Pending</th>
             <th>Within <br/>10<br/>days</th>
            <th>Between <br/>11 to 15<br/> days</th>
            <th>Between <br/>16 to 30<br/> days</th>
            <th>Between <br/> 31 to 90<br/> days</th>
            <th>Between <br/>91 to 180<br/> days</th>
            <th>Between <br/>181 to 365<br/> days</th>
            <th>Above <br/>01<br/> year</th>
          </tr>
        </thead>
<tbody>
  {stationRows.map((r, i) => {
    const isLastRow = i === stationRows.length - 1;

    const renderCount = (value, daysRange) => (
      <td className="td-count">
        {isLastRow ? (
          <span>{value}</span>
        ) : (
          <Link
            to={detailLink(r.typeKey, daysRange)}
            className="count-link"
          >
            {value}
          </Link>
        )}
      </td>
    );

    return (
      <tr
        key={i}
        className={i % 2 === 1 ? 'alt-row' : ''}
      >
        <td className="td-sno">{r.sno || i + 1}</td>

        <td className="td-app-type">
          {r.ApplicationType}
        </td>

        {renderCount(r.Total)}
        {renderCount(r.Within10Days, '0-10')}
        {renderCount(r.Between11To15Days, '11-15')}
        {renderCount(r.Between16To30Days, '16-30')}
        {renderCount(r.Between31To90Days, '31-90')}
        {renderCount(r.Between91To180Days, '91-180')}
        {renderCount(r.Between181To365Days, '181-365')}
        {renderCount(r.Above01Year, '365+')}
      </tr>
    );
  })}
</tbody>
      </table>
    </div>
  );
}
